import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '@/components/ui/Button';

/**
 * UnauthorizedPage — shown when a user tries to access a route
 * they don't have the required role for.
 *
 * Displays the user's current roles and explains the access restriction.
 */
export function UnauthorizedPage(): React.ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div
          className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6 bg-fail-soft"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-fail"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-semibold text-ink mb-2">Access Restricted</h1>
        <p className="text-ink-muted text-sm mb-6">
          Your current role does not have permission to view this page. Contact your administrator
          if you believe this is an error.
        </p>

        {/* Current roles */}
        {user !== null && (
          <div className="bg-white rounded-lg border border-line p-4 mb-6 text-left">
            <p className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
              Your current roles
            </p>
            <div className="flex flex-wrap gap-2">
              {user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-info-soft text-info"
                  >
                    {role.replace(/_/g, ' ')}
                  </span>
                ))
              ) : (
                <span className="text-sm text-ink-muted">No roles assigned</span>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
            Go back
          </Button>
          <Button size="sm" onClick={() => navigate('/members', { replace: true })}>
            Go to Members
          </Button>
        </div>
      </div>
    </div>
  );
}
